import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Plus,
    Sparkle,
    Trash,
    PencilSimple,
    CheckCircle,
    XCircle,
    Clock,
    PlayCircle,
    Star,
    X,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminSampleVideosPanel — admin CRUD for the public Sample Videos gallery.
 *
 * Two ways to add a sample:
 *   1. MANUAL — paste a YouTube/Vimeo/mp4 URL (e.g., a Loom you recorded).
 *   2. SORA 2 — write a prompt; backend generates an mp4 in 2-5 minutes.
 *
 * Generations stay unpublished by default; admin reviews then toggles publish.
 * Polling refreshes the list every 15s while any video is in `generating` state.
 */
export default function AdminSampleVideosPanel() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showManual, setShowManual] = useState(false);
    const [showGenerate, setShowGenerate] = useState(false);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/sample-videos");
            setVideos(r.data || []);
        } catch (err) {
            toast.error("Failed to load sample videos");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Auto-poll while any generation is in progress
    useEffect(() => {
        const inProgress = videos.some((v) => v.status === "generating");
        if (!inProgress) return;
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [videos, load]);

    const onTogglePublish = async (v) => {
        try {
            await api.put(`/admin/sample-videos/${v.id}`, { is_published: !v.is_published });
            toast.success(v.is_published ? "Unpublished" : "Published");
            load();
        } catch (err) {
            toast.error("Update failed");
        }
    };

    const onToggleWalkthrough = async (v) => {
        try {
            await api.put(`/admin/sample-videos/${v.id}`, { is_walkthrough: !v.is_walkthrough });
            toast.success(v.is_walkthrough ? "Unmarked as walkthrough" : "Marked as the walkthrough video");
            load();
        } catch (err) {
            toast.error("Update failed");
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this sample video permanently? This also removes the generated mp4 file.")) return;
        try {
            await api.delete(`/admin/sample-videos/${id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="admin-sample-videos-panel">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                <div>
                    <p className="text-sm text-muted-foreground">
                        Showcase library that prospects see on the landing page. Generate fresh
                        clips with Sora 2, or paste a YouTube / Vimeo URL of a Recipe-built video.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="admin-sample-add-manual-btn"
                        onClick={() => setShowManual(true)}
                        className="border border-electric-blue/60 hover:border-electric-blue hover:bg-electric-blue/10 text-electric-blue label-mono text-[11px] px-4 py-2.5 transition-all flex items-center gap-2"
                    >
                        <Plus size={14} weight="bold" /> ADD URL
                    </button>
                    <button
                        data-testid="admin-sample-generate-btn"
                        onClick={() => setShowGenerate(true)}
                        className="bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-4 py-2.5 text-[11px] tracking-wider uppercase transition-all hover:shadow-[0_0_20px_rgba(0,123,255,0.5)] flex items-center gap-2"
                    >
                        <Sparkle size={14} weight="fill" /> GENERATE WITH SORA 2
                    </button>
                </div>
            </div>

            <div className="bg-[#0A0A0A] border border-white/10">
                <table className="w-full">
                    <thead className="border-b border-white/10">
                        <tr>
                            <th className="text-left px-5 py-3 label-mono text-[10px]">PREVIEW</th>
                            <th className="text-left px-5 py-3 label-mono text-[10px]">TITLE / NICHE</th>
                            <th className="text-left px-5 py-3 label-mono text-[10px]">SOURCE</th>
                            <th className="text-left px-5 py-3 label-mono text-[10px]">STATUS</th>
                            <th className="text-left px-5 py-3 label-mono text-[10px]">FLAGS</th>
                            <th className="text-right px-5 py-3 label-mono text-[10px]">ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={6} className="p-6 label-mono text-muted-foreground">LOADING...</td></tr>
                        )}
                        {!loading && videos.length === 0 && (
                            <tr><td colSpan={6} className="p-6 label-mono text-muted-foreground">
                                NO SAMPLE VIDEOS YET — click GENERATE WITH SORA 2 or ADD URL to seed the gallery.
                            </td></tr>
                        )}
                        {videos.map((v, i) => (
                            <tr
                                key={v.id}
                                data-testid={`admin-sample-row-${i}`}
                                className="border-b border-white/5 hover:bg-electric-blue/5 transition-colors"
                            >
                                <td className="px-5 py-4 w-32">
                                    <PreviewThumb video={v} />
                                </td>
                                <td className="px-5 py-4 max-w-md">
                                    <p className="font-semibold text-sm truncate">{v.title}</p>
                                    <p className="text-xs text-electric-blue label-mono mt-0.5">{v.niche}</p>
                                    {v.description && (
                                        <p className="text-xs text-muted-foreground truncate mt-1">{v.description}</p>
                                    )}
                                </td>
                                <td className="px-5 py-4">
                                    {v.source === "sora2" ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] label-mono border border-electric-blue/40 bg-electric-blue/5 text-electric-blue">
                                            <Sparkle size={10} weight="fill" /> SORA 2
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2 py-1 text-[10px] label-mono border border-white/15 text-muted-foreground">
                                            URL
                                        </span>
                                    )}
                                </td>
                                <td className="px-5 py-4">
                                    <StatusBadge status={v.status} error={v.error} />
                                </td>
                                <td className="px-5 py-4">
                                    <div className="flex flex-wrap gap-1.5">
                                        {v.is_walkthrough && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] label-mono border border-yellow-400/40 bg-yellow-500/5 text-yellow-300">
                                                <Star size={10} weight="fill" /> WALKTHROUGH
                                            </span>
                                        )}
                                        {v.is_published ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] label-mono border border-emerald-500/40 bg-emerald-500/5 text-emerald-300">
                                                <CheckCircle size={10} weight="fill" /> PUBLISHED
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] label-mono border border-white/15 text-muted-foreground">
                                                <XCircle size={10} weight="fill" /> DRAFT
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-5 py-4">
                                    <div className="flex items-center justify-end gap-1">
                                        <ActionButton
                                            onClick={() => onToggleWalkthrough(v)}
                                            title={v.is_walkthrough ? "Unmark as walkthrough" : "Mark as the assembly-line walkthrough video"}
                                            testId={`admin-sample-walkthrough-${i}`}
                                            disabled={v.status !== "ready"}
                                        >
                                            <Star size={13} weight={v.is_walkthrough ? "fill" : "bold"} className={v.is_walkthrough ? "text-yellow-300" : ""} />
                                        </ActionButton>
                                        <ActionButton
                                            onClick={() => onTogglePublish(v)}
                                            title={v.is_published ? "Unpublish" : "Publish"}
                                            testId={`admin-sample-publish-${i}`}
                                            disabled={v.status !== "ready"}
                                        >
                                            {v.is_published
                                                ? <XCircle size={13} weight="bold" />
                                                : <CheckCircle size={13} weight="bold" />}
                                        </ActionButton>
                                        <ActionButton
                                            onClick={() => setEditing(v)}
                                            title="Edit metadata"
                                            testId={`admin-sample-edit-${i}`}
                                        >
                                            <PencilSimple size={13} weight="bold" />
                                        </ActionButton>
                                        <ActionButton
                                            onClick={() => onDelete(v.id)}
                                            title="Delete"
                                            testId={`admin-sample-delete-${i}`}
                                            danger
                                        >
                                            <Trash size={13} weight="bold" />
                                        </ActionButton>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showManual && (
                <ManualAddModal
                    onClose={() => setShowManual(false)}
                    onSaved={() => { setShowManual(false); load(); }}
                />
            )}
            {showGenerate && (
                <GenerateModal
                    onClose={() => setShowGenerate(false)}
                    onStarted={() => { setShowGenerate(false); load(); }}
                />
            )}
            {editing && (
                <EditModal
                    video={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function PreviewThumb({ video }) {
    const url = video.video_url || "";
    const resolved = url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url;
    const direct = /\.(mp4|webm|mov)(\?|$)/i.test(resolved) || resolved.includes("/api/sample-videos/file/");

    if (video.status !== "ready" || !resolved) {
        return (
            <div className="w-24 aspect-[9/16] bg-black/60 border border-white/10 flex items-center justify-center">
                <Clock size={20} className="text-muted-foreground" />
            </div>
        );
    }
    if (direct) {
        return (
            <div className="w-24 aspect-[9/16] bg-black border border-white/10 overflow-hidden relative group">
                <video src={resolved} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-all">
                    <PlayCircle size={20} weight="fill" className="text-electric-blue" />
                </div>
            </div>
        );
    }
    return (
        <div className="w-24 aspect-[9/16] bg-black/60 border border-white/10 flex items-center justify-center text-electric-blue">
            <PlayCircle size={20} weight="fill" />
        </div>
    );
}

function StatusBadge({ status, error }) {
    if (status === "ready") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] label-mono border border-emerald-500/40 bg-emerald-500/5 text-emerald-300">
                <CheckCircle size={10} weight="fill" /> READY
            </span>
        );
    }
    if (status === "generating") {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] label-mono border border-electric-blue/40 bg-electric-blue/5 text-electric-blue">
                <Clock size={10} weight="fill" /> GENERATING
            </span>
        );
    }
    if (status === "failed") {
        return (
            <span
                title={error || "Generation failed"}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] label-mono border border-red-500/40 bg-red-500/5 text-red-300"
            >
                <XCircle size={10} weight="fill" /> FAILED
            </span>
        );
    }
    return <span className="text-xs text-muted-foreground">{status || "—"}</span>;
}

function ActionButton({ children, onClick, title, testId, danger, disabled }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            title={title}
            disabled={disabled}
            className={`p-2 border transition-colors ${
                disabled
                    ? "border-white/5 text-muted-foreground/30 cursor-not-allowed"
                    : danger
                    ? "border-white/10 text-muted-foreground hover:text-red-400 hover:border-red-400"
                    : "border-white/10 text-muted-foreground hover:text-electric-blue hover:border-electric-blue"
            }`}
        >
            {children}
        </button>
    );
}

// ---------- Modals ----------
function ModalShell({ title, onClose, children, testId }) {
    return (
        <div
            data-testid={testId}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl bg-[#0A0A0A] border border-electric-blue/40 neon-border max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                    <h3 className="font-heading font-bold text-lg">{title}</h3>
                    <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-white border border-white/10">
                        <X size={16} weight="bold" />
                    </button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
}

function ManualAddModal({ onClose, onSaved }) {
    const [form, setForm] = useState({
        title: "",
        niche: "Historical Mystery",
        description: "",
        video_url: "",
        is_walkthrough: false,
        is_published: true,
        display_order: 0,
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title || !form.video_url) {
            toast.error("Title and Video URL are required");
            return;
        }
        setSaving(true);
        try {
            await api.post("/admin/sample-videos", form);
            toast.success("Sample video added");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalShell title="Add sample video by URL" onClose={onClose} testId="admin-sample-manual-modal">
            <form onSubmit={submit} className="space-y-4">
                <Field label="Title" required>
                    <input
                        data-testid="admin-sample-manual-title"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        placeholder="The Vanished Empire — Door That Wasn't Supposed to Open"
                    />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Niche" required>
                        <input
                            data-testid="admin-sample-manual-niche"
                            value={form.niche}
                            onChange={(e) => setForm({ ...form, niche: e.target.value })}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                            placeholder="Historical Mystery"
                        />
                    </Field>
                    <Field label="Display order">
                        <input
                            type="number"
                            data-testid="admin-sample-manual-order"
                            value={form.display_order}
                            onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || 0, 10) })}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        />
                    </Field>
                </div>
                <Field label="Video URL" required hint="YouTube embed URL, Vimeo, Loom embed, or direct .mp4">
                    <input
                        data-testid="admin-sample-manual-url"
                        value={form.video_url}
                        onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                        className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        placeholder="https://www.youtube.com/embed/XXXXXX"
                    />
                </Field>
                <Field label="Description">
                    <textarea
                        data-testid="admin-sample-manual-desc"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none min-h-[80px]"
                        placeholder="60-second hook → reveal → CTA. Built from Recipe Card #001."
                    />
                </Field>
                <div className="flex items-center gap-5 text-sm">
                    <Toggle
                        label="Walkthrough video"
                        hint="Featured as the start-to-finish demo on Landing"
                        checked={form.is_walkthrough}
                        onChange={(v) => setForm({ ...form, is_walkthrough: v })}
                        testId="admin-sample-manual-walkthrough"
                    />
                    <Toggle
                        label="Publish immediately"
                        checked={form.is_published}
                        onChange={(v) => setForm({ ...form, is_published: v })}
                        testId="admin-sample-manual-publish"
                    />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="label-mono text-[11px] px-4 py-2.5 border border-white/15 hover:border-white/30">
                        CANCEL
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="admin-sample-manual-save"
                        className="bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-5 py-2.5 text-[11px] tracking-wider uppercase disabled:opacity-50"
                    >
                        {saving ? "SAVING..." : "ADD SAMPLE"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function GenerateModal({ onClose, onStarted }) {
    const [form, setForm] = useState({
        title: "",
        niche: "Historical Mystery",
        prompt: "",
        description: "",
        duration: 4,
        size: "1280x720",  // sora-2 (default model) only supports this; sora-2-pro adds vertical
        model: "sora-2",
        is_walkthrough: false,
        display_order: 0,
    });
    const [saving, setSaving] = useState(false);

    // Per-model size options. Mirror of backend SORA2_SIZES — keep in sync.
    // sora-2 (standard) only supports landscape; for vertical 9:16 (TikTok/Shorts native) use sora-2-pro.
    const sizeOptions = form.model === "sora-2-pro"
        ? [
            ["1024x1792", "9:16 vertical (1024x1792) — TikTok/Shorts"],
            ["1280x720", "16:9 HD (1280x720)"],
            ["1792x1024", "16:9 wide (1792x1024)"],
            ["1024x1024", "1:1 square (1024x1024)"],
        ]
        : [
            ["1280x720", "16:9 HD (1280x720) — only size sora-2 supports"],
        ];

    // Switching model? snap size to the new model's first allowed value if needed.
    const onModelChange = (m) => {
        const allowed = m === "sora-2-pro"
            ? ["1024x1792", "1280x720", "1792x1024", "1024x1024"]
            : ["1280x720"];
        setForm((f) => ({
            ...f,
            model: m,
            size: allowed.includes(f.size) ? f.size : allowed[0],
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title || !form.prompt) {
            toast.error("Title and Prompt are required");
            return;
        }
        setSaving(true);
        try {
            await api.post("/admin/sample-videos/generate", form);
            toast.success("Sora 2 generation started — refresh in 2-5 minutes.");
            onStarted();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Generation request failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalShell title="Generate sample video with Sora 2" onClose={onClose} testId="admin-sample-generate-modal">
            <form onSubmit={submit} className="space-y-4">
                <div className="px-3 py-2 bg-electric-blue/5 border border-electric-blue/30 text-xs text-foreground/80 leading-relaxed">
                    <strong className="text-electric-blue">Heads up:</strong> generation takes ~2-5 min for sora-2 / 4s, longer for sora-2-pro. Each call deducts from your Emergent Universal Key balance. The result lands as a draft &mdash; review, then publish. <span className="text-electric-blue">For vertical 9:16 (TikTok / Shorts), pick sora-2-pro &middot; 1024x1792.</span>
                </div>
                <Field label="Title" required>
                    <input
                        data-testid="admin-sample-gen-title"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        placeholder="The Vanished Empire — Sample Hook"
                    />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Niche" required>
                        <input
                            data-testid="admin-sample-gen-niche"
                            value={form.niche}
                            onChange={(e) => setForm({ ...form, niche: e.target.value })}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                            placeholder="Historical Mystery"
                        />
                    </Field>
                    <Field label="Display order">
                        <input
                            type="number"
                            data-testid="admin-sample-gen-order"
                            value={form.display_order}
                            onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || 0, 10) })}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        />
                    </Field>
                </div>
                <Field label="Sora 2 prompt" required hint="Describe the visuals + camera + lighting. Be cinematic.">
                    <textarea
                        data-testid="admin-sample-gen-prompt"
                        value={form.prompt}
                        onChange={(e) => setForm({ ...form, prompt: e.target.value })}
                        className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none min-h-[120px]"
                        placeholder="Cinematic documentary footage, 8k photoreal, ancient stone door deep inside an Egyptian tomb, ethereal blue-hour fog, slow drone push-in, anamorphic lens flare, hyper-detailed textures, vertical 9:16 framing"
                    />
                </Field>
                <div className="grid grid-cols-3 gap-3">
                    <Field label="Model">
                        <select
                            data-testid="admin-sample-gen-model"
                            value={form.model}
                            onChange={(e) => onModelChange(e.target.value)}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        >
                            <option value="sora-2">sora-2 (faster)</option>
                            <option value="sora-2-pro">sora-2-pro (higher quality)</option>
                        </select>
                    </Field>
                    <Field label="Aspect / Size">
                        <select
                            data-testid="admin-sample-gen-size"
                            value={form.size}
                            onChange={(e) => setForm({ ...form, size: e.target.value })}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        >
                            {sizeOptions.map(([v, label]) => (
                                <option key={v} value={v}>{label}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Duration">
                        <select
                            data-testid="admin-sample-gen-duration"
                            value={form.duration}
                            onChange={(e) => setForm({ ...form, duration: parseInt(e.target.value, 10) })}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        >
                            <option value={4}>4 seconds</option>
                            <option value={8}>8 seconds</option>
                            <option value={12}>12 seconds</option>
                        </select>
                    </Field>
                </div>
                <Field label="Description (shown under the card)">
                    <input
                        data-testid="admin-sample-gen-desc"
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        placeholder="Generated from Recipe Card #001 master prompt."
                    />
                </Field>
                <Toggle
                    label="Use as the assembly-line walkthrough video"
                    hint="Featured at the top of the Walkthrough section on Landing"
                    checked={form.is_walkthrough}
                    onChange={(v) => setForm({ ...form, is_walkthrough: v })}
                    testId="admin-sample-gen-walkthrough"
                />
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="label-mono text-[11px] px-4 py-2.5 border border-white/15 hover:border-white/30">
                        CANCEL
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="admin-sample-gen-submit"
                        className="bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-5 py-2.5 text-[11px] tracking-wider uppercase disabled:opacity-50 flex items-center gap-2"
                    >
                        <Sparkle size={12} weight="fill" /> {saving ? "STARTING..." : "START GENERATION"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function EditModal({ video, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: video.title || "",
        niche: video.niche || "",
        description: video.description || "",
        video_url: video.source === "external" ? (video.video_url || "") : "",
        is_walkthrough: !!video.is_walkthrough,
        is_published: !!video.is_published,
        display_order: video.display_order ?? 0,
    });
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = { ...form };
            // For Sora 2 generations, video_url is computed by the backend; don't allow overriding.
            if (video.source === "sora2") delete payload.video_url;
            await api.put(`/admin/sample-videos/${video.id}`, payload);
            toast.success("Updated");
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <ModalShell title="Edit sample video" onClose={onClose} testId="admin-sample-edit-modal">
            <form onSubmit={submit} className="space-y-4">
                <Field label="Title">
                    <input
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                    />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Niche">
                        <input
                            value={form.niche}
                            onChange={(e) => setForm({ ...form, niche: e.target.value })}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        />
                    </Field>
                    <Field label="Display order">
                        <input
                            type="number"
                            value={form.display_order}
                            onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value || 0, 10) })}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        />
                    </Field>
                </div>
                {video.source === "external" && (
                    <Field label="Video URL">
                        <input
                            value={form.video_url}
                            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                            className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none"
                        />
                    </Field>
                )}
                <Field label="Description">
                    <textarea
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-electric-blue outline-none min-h-[80px]"
                    />
                </Field>
                <div className="flex items-center gap-5 text-sm">
                    <Toggle
                        label="Walkthrough video"
                        checked={form.is_walkthrough}
                        onChange={(v) => setForm({ ...form, is_walkthrough: v })}
                    />
                    <Toggle
                        label="Published"
                        checked={form.is_published}
                        onChange={(v) => setForm({ ...form, is_published: v })}
                    />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="label-mono text-[11px] px-4 py-2.5 border border-white/15 hover:border-white/30">
                        CANCEL
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid="admin-sample-edit-save"
                        className="bg-electric-blue hover:bg-electric-blue-dark text-white font-bold px-5 py-2.5 text-[11px] tracking-wider uppercase disabled:opacity-50"
                    >
                        {saving ? "SAVING..." : "SAVE"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function Field({ label, required, hint, children }) {
    return (
        <label className="block">
            <span className="label-mono text-[10px] text-electric-blue">
                {label}{required && <span className="text-red-400 ml-1">*</span>}
            </span>
            {hint && <span className="block text-[11px] text-muted-foreground mb-1.5 mt-0.5">{hint}</span>}
            <div className="mt-1">{children}</div>
        </label>
    );
}

function Toggle({ label, hint, checked, onChange, testId }) {
    return (
        <label className="inline-flex items-start gap-2 cursor-pointer">
            <input
                type="checkbox"
                data-testid={testId}
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-0.5 accent-electric-blue"
            />
            <span>
                <span className="text-sm">{label}</span>
                {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
            </span>
        </label>
    );
}
